import { ModelTreeNode } from "@/types/ModelTreeNode";
import { CadModelCategory, ComponentDto, CategoryDto, ElementDto, ExtendedDataKey, ModelExtendedDataDto } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useProjectModel } from "@api/models";
import useAppStore from "@hooks/useAppStore";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { visitNodes } from "@utils/treeNodes";
import { MutableRefObject, useEffect, useMemo, useState } from "react";
import useMultiplier from "@features/analysis/utils/multiplier";

export interface UseModelProps {
  apiRef: MutableRefObject<GridApiPremium>;
  modelId: string | undefined;
  dataVersion?: number;
}

const useModel = (props: UseModelProps) => {
  const { modelId, apiRef, dataVersion } = props;
  const [hasCo2MaterialTypeSpecs, setHasCo2MaterialTypeSpecs] = useState(false);
  const [co2MaterialTypeSpecKind, setCo2MaterialTypeSpecKind] = useState<string | null>(null);
  const appStore = useAppStore();
  const cadModelCategories = useCadModelCategories();
  const projectModel = useProjectModel(props.modelId, undefined, { enabled: !!props.modelId });
  const { applyMultiplierTotals } = useMultiplier();

  const modelCategories = projectModel.data?.categories;
  const modelElements = projectModel.data?.elements;
  const modelComponents = projectModel.data?.components;
  const modelExtendedData = projectModel.data?.modelExtendedData;

  const loading = useMemo(() => {
    const isLoading = cadModelCategories.isFetching || projectModel.isFetching;
    return isLoading;
  }, [cadModelCategories.isFetching, projectModel.isFetching, modelId, modelElements, modelComponents]);

  useEffect(() => {
    if (!projectModel.data || !cadModelCategories.data) {
      return;
    }

    // check if modelExtendedData has any Co2MaterialTypeSpec values
    const hasCo2MaterialTypeSpec = (modelExtendedData && Object.values(modelExtendedData).some((data) => data?.hasOwnProperty(ExtendedDataKey.Co2MaterialTypeSpec))) || false;
    setHasCo2MaterialTypeSpecs(hasCo2MaterialTypeSpec);

    // Extract Kind property from Co2MaterialTypeSpec using null coalescing
    const extractedKind = Object.values(modelExtendedData ?? {})
      .find(data => data?.[ExtendedDataKey.Co2MaterialTypeSpec])
      ?.[ExtendedDataKey.Co2MaterialTypeSpec]?.Kind ?? null;
    setCo2MaterialTypeSpecKind(extractedKind);

    const cadModelCategoriesLookup = cadModelCategories.data?.reduce((acc, c) => {
      if (c.cadModelCategoryId) {
        acc.set(c.cadModelCategoryId, c);
      }
      return acc;
    }, new Map<string, CadModelCategory>());

    updateElementData(modelElements || [], cadModelCategoriesLookup, hasCo2MaterialTypeSpec, modelExtendedData);
    updateComponentData(modelComponents || [], cadModelCategoriesLookup, hasCo2MaterialTypeSpec, modelExtendedData);

    updateAssignNodes();
  }, [projectModel.data, cadModelCategories.data]);

  // This is for debugging and will add a prefix to the labels to identify type
  const enablePrefix = false;
  const caPrefix = enablePrefix ? "ca:" : ""; // Category
  const gPrefix = enablePrefix ? "g:" : ""; // Group
  const ePrefix = enablePrefix ? "e:" : ""; // Element
  const coPrefix = enablePrefix ? "co:" : ""; // Component

  const updateElementData = (
    elements: ElementDto[],
    cadModelCategories: Map<string, CadModelCategory> | null,
    hasCo2MaterialTypes: boolean,
    modelExtendedData?: ModelExtendedDataDto
  ) => {
    if ((elements?.length || 0) > 0) {
      elements.forEach((e: ElementDto) => {
        const cadModelCategory = cadModelCategories?.get(e.cadModelCategoryId!);
        if (cadModelCategory) {
          e.cadModelCategory = cadModelCategory;
        }

        if (hasCo2MaterialTypes && modelExtendedData) {
          const extendedData = modelExtendedData[e.elementId!];
          if (extendedData) {
            e.co2MaterialTypeSpec = extendedData[ExtendedDataKey.Co2MaterialTypeSpec];
          }
        }
      });

      appStore.queryClient.setQueryData([["elements", modelId]], () => {
        return { data: elements };
      });
    }
  };

  const updateComponentData = (
    components: ComponentDto[],
    cadModelCategories: Map<string, CadModelCategory> | null,
    hasCo2MaterialTypes: boolean,
    modelExtendedData?: ModelExtendedDataDto
  ) => {
    if ((components?.length || 0) > 0) {
      components?.forEach((c: ComponentDto) => {
        const cadModelCategory = cadModelCategories?.get(c.cadModelCategoryId!);
        if (cadModelCategory) {
          c.cadModelCategory = cadModelCategory;
        }

        if (hasCo2MaterialTypes && modelExtendedData) {
          const extendedData = modelExtendedData[c.componentId!];
          if (extendedData) {
            c.co2MaterialTypeSpec = extendedData[ExtendedDataKey.Co2MaterialTypeSpec];
          }
        }
      });
    }

    appStore.queryClient.setQueryData([["components", modelId]], () => {
      return { data: components };
    });
  };

  const updateAssignNodes = () => {
    if (!projectModel.data || !cadModelCategories.data) {
      console.warn("updateAssignNodes: Missing required data, skipping update");
      return;
    }

    if (!apiRef?.current) {
      console.warn("updateAssignNodes: apiRef not available, skipping update");
      return;
    }

    let treeNodes: ModelTreeNode[] =
      modelCategories?.map((c: CategoryDto) => {
        const categoryChildren =
          modelElements
            ?.filter((e: ElementDto) => c.cadModelCategoryId === e.cadModelCategoryId)
            .map((e: ElementDto) => {
              e.siblings = e.componentCount || 1;
              e.label = ePrefix + e.cadName;
              return {
                type: "element",
                key: e.elementId,
                data: {
                  ...e,
                  systemId: (e as any).systemId || "",
                  subSystemId: (e as any).subSystemId || "",
                  attribute1: (e as any).attribute1 || "",
                  attribute2: (e as any).attribute2 || "",
                  attribute3: (e as any).attribute3 || "",
                  hasChildMultiplierOverride: (e as any).hasChildMultiplierOverride,
                },
                leaf: e.componentCount === 0,
              };
            }) || null;

        // build family/group nodes and merge modelExtendedData where present
        let families = null;
        if (categoryChildren) {
          const familyGroups = groupBy(categoryChildren, (n: any) => n.data.cadFamily || "");
          families = processGroupNodes(familyGroups, c.cadModelCategoryId);
          families.forEach((family: any, familyIndex: number) => {
            const elementGroup = groupBy(family.children, (n: any) => {
              const prefix = n.data.cadFamily === n.data.cadName ? `${c.cadModelCategoryId}/` : "";
              return prefix + (n.data.cadName || "").replace("/", "");
            });
            family.children = processGroupNodes(elementGroup, `${c.cadModelCategoryId}/${familyIndex.toString()}`, 1);
            family.data.assigned = family.children.reduce((acc: boolean, e: any) => acc && e.data.assigned, true);

            if (modelExtendedData && family.key) {
              const possibleKeys = [
                family.key,
                family.data.label,
                `${c.cadModelCategoryId}/${family.data.label}`,
                family.data.label?.split('_').slice(1).join('_'),
              ].filter(Boolean);

              let groupModelData = null;
              let matchedKey = null;

              for (const key of possibleKeys) {
                if (key && modelExtendedData[key]) {
                  groupModelData = modelExtendedData[key];
                  matchedKey = key;
                  break;
                }
              }

              if (groupModelData && matchedKey) {
                family.data = {
                  ...family.data,

                  // TODO: this should be a model
                  // can then replace all of this with a spread operator ...groupModelData
                  multiplierInput: groupModelData.MultiplierInput || "",
                  multiplierValue: groupModelData.MultiplierValue ? parseFloat(groupModelData.MultiplierValue) : null,
                  notes: groupModelData.Notes || "",
                  systemId: groupModelData.SystemId || "",
                  subSystemId: groupModelData.SubSystemId || "",
                  attribute1: groupModelData.Attribute1 || "",
                  attribute2: groupModelData.Attribute2 || "",
                  attribute3: groupModelData.Attribute3 || "",
                  hasChildMultiplierOverride: groupModelData.HasChildMultiplierOverride ? (groupModelData.HasChildMultiplierOverride.toLowerCase() === "true") : false,
                  carbonImpactInput: groupModelData.CarbonImpactInput || "",
                  carbonImpactValue: groupModelData.CarbonImpactValue ? parseFloat(groupModelData.CarbonImpactValue) : null,
                  carbonImpactNotes: groupModelData.CarbonImpactNotes || "",
                  hasChildCarbonImpactOverride: groupModelData.HasChildCarbonImpactOverride ? (groupModelData.HasChildCarbonImpactOverride.toLowerCase() === "true") : false
                };
              } else {
                family.data.hasChildMultiplierOverride = false;
                family.data.HasChildCarbonImpactOverride = false;
              }
            }

            if (modelExtendedData && family.children) {
              family.children.forEach((child: any) => {
                if (child.type === "group" && child.key && modelExtendedData[child.key]) {
                  const childModelData = modelExtendedData[child.key];
                  child.data = {
                    ...child.data,
                    // TODO: this should be a model
                    // can then replace all of this with a spread operator ...childModelData
                    multiplierInput: childModelData.MultiplierInput || "",
                    multiplierValue: childModelData.MultiplierValue ? parseFloat(childModelData.MultiplierValue) : null,
                    notes: childModelData.Notes || "",
                    systemId: childModelData.SystemId || "",
                    subSystemId: childModelData.SubSystemId || "",
                    attribute1: childModelData.Attribute1 || "",
                    attribute2: childModelData.Attribute2 || "",
                    attribute3: childModelData.Attribute3 || "",
                    hasChildMultiplierOverride: childModelData.HasChildMultiplierOverride ? (childModelData.HasChildMultiplierOverride.toLowerCase() === "true") : false,
                    carbonImpactInput: childModelData.CarbonImpactInput || "",
                    carbonImpactValue: childModelData.CarbonImpactValue ? parseFloat(childModelData.CarbonImpactValue) : null,
                    carbonImpactNotes: childModelData.CarbonImpactNotes || "",
                    hasChildCarbonImpactOverride: childModelData.HasChildCarbonImpactOverride ? (childModelData.HasChildCarbonImpactOverride.toLowerCase() === "true") : false
                  };
                }
              });
            }
          });
          families.sort((a: any, b: any) => (a.data.label > b.data.label ? 1 : -1));
        }

        return {
          key: c.cadModelCategoryId,
          type: "category",
          data: {
            ...c,
            label: caPrefix + cadModelCategories.data?.find((cmc) => cmc.cadModelCategoryId === c.cadModelCategoryId)?.label,
            siblings: c.elementCount,
            // TODO: if these values are already part of c then why duplicate them here? The grid does not require "" in-place of null
            multiplierInput: c.multiplierInput || "",
            multiplierValue: c.multiplierValue || null,
            notes: c.notes || "",
            systemId: c.systemId || "",
            subSystemId: c.subSystemId || "",
            attribute1: c.attribute1 || "",
            attribute2: c.attribute2 || "",
            attribute3: c.attribute3 || "",
            hasChildMultiplierOverride: c.hasChildMultiplierOverride,
            carbonImpactInput: c.carbonImpactInput || "",
            carbonImpactValue: c.carbonImpactValue || null,
            carbonImpactNotes: c.carbonImpactNotes || "",
            hasChildCarbonImpactOverride: c.hasChildCarbonImpactOverride
          },
          children: families,
          leaf: c.elementCount === 0,
        } as ModelTreeNode;
      }) || [];

    if (!treeNodes || !Array.isArray(treeNodes)) {
      console.warn("updateAssignNodes: Invalid treeNodes, skipping update");
      return;
    }

    treeNodes.sort((a: any, b: any) => (a.data.label > b.data.label ? 1 : -1));

    // Restore child components
    if (modelComponents) {
      visitNodes(treeNodes as ModelTreeNode[], (childNodes: ModelTreeNode[], node: ModelTreeNode) => {
        if (node.type === "element") {
          assignComponentNodes(childNodes, node, modelComponents as any[], node?.data?.elementId);
        }
      });
    }

    const rowNodes = flattenizeTree(treeNodes, []);
    apiRef?.current?.updateRows(rowNodes)
    const validNodes = rowNodes?.filter((node) => {
      if (!node || typeof node !== "object") {
        console.warn("Invalid node detected:", node);
        return false;
      }
      if ((node as any).depth === undefined || (node as any).depth === null) {
        console.warn("Node missing depth property:", node);
        return false;
      }
      return true;
    });

    // IMPORTANT: applyMultiplierTotals now only enforces original stored totals into display totals
    const nodesWithMultiplierTotals = applyMultiplierTotals(validNodes);

    appStore.setAssignNodes(nodesWithMultiplierTotals as ModelTreeNode[]);

    const nodesToUpdate = appStore.assignNodes || [];
    const invalidUpdateNodes = nodesToUpdate.filter((node) => !node || (node as any).depth === undefined);

    if (invalidUpdateNodes.length > 0) {
      console.error("Found invalid nodes in assignNodes:", invalidUpdateNodes);
      return;
    }

    apiRef.current.updateRows?.(nodesToUpdate);
    apiRef.current?.unstable_applyFilters?.();
  };

  useEffect(() => {
    if (appStore.assignNodes === null && !!cadModelCategories.data && !!projectModel.data) {
      updateAssignNodes();
    }
  }, [appStore.assignNodesRecalc, cadModelCategories.data, projectModel.data]);

  const join = (value: string | undefined) => {
    if (value) return value + "/";
    return "";
  };

  const appendPath = (path: string[], key: string | number | undefined) => {
    if (key) path.push(key.toString());
    return path;
  };

  const flattenizeTree = (nodes: ModelTreeNode[] | undefined, path: string[]) => {
    let rows: any[] = [];

    if (nodes) {
      nodes.forEach((node: ModelTreeNode, index: number) => {
        path = appendPath(path, `${index}`);
        node.path = [...path];
        node.depth = path.length - 1;
        rows.push(node);

        const childNodes = flattenizeTree(node.children, path);
        if (childNodes.length > 0) {
          rows = rows.concat(childNodes);
        }

        path.pop();
      });
    }
    return rows;
  };

  const processGroupNodes = (nodes: any, path: string | undefined, minGroupSize: number = -1) => {
    return Object.keys(nodes).reduce((acc: any[], key: string) => {
      if (minGroupSize !== -1 && nodes[key].length <= minGroupSize) {
        nodes[key].forEach((node: any) => {
          node.key = `${join(path)}${key}`;
          acc.push(node);
        });
      } else {
        const displayLabel = key.includes("/") ? key.substring(key.lastIndexOf("/") + 1) : key;
        const node = {
          type: "group",
          key: `${join(gPrefix)}${join(path)}${key}`,
          data: {
            label: displayLabel,
            assigned: nodes[key].reduce((acc: boolean, e: any) => {
              return acc && e.data.assigned;
            }, true),

            hasChildMultiplierOverride: nodes[key].some(
              (e: any) => e.data.hasChildMultiplierOverride === true
            ),
          },
          children: nodes[key].sort((a: any, b: any) => a.data.label?.localeCompare(b.data.label) || (a.data.cadId ? a.data.cadId.localeCompare(b.data.cadId) : 1)),
          leaf: nodes[key].length === 0,
        };
        acc.push(node);
      }
      return acc;
    }, []);
  };

  const assignComponentNodes = (assignNodes: ModelTreeNode[], node: any, data: any[], parentId: string) => {
    if (assignNodes.length === 0 || !node || !parentId) return;

    const children = data.filter((c: any) => c.elementId === parentId);
    if (children.length > 0) {
      node.children = createComponentNodes(children);
      node.leaf = node.children.length === 0;
      replaceNode(assignNodes as any[], "key", parentId, node);

      if (node.children) {
        node.data.assigned = node.children.reduce((acc: boolean, e: any) => acc && e.data.assigned, true);
      }
    }
  };

  const createComponentNodes = (nodes: any[]) => {
    const components = nodes.map((c: any) => {
      c.label = coPrefix + c.cadName;

      return {
        key: c.componentId,
        type: "component",
        data: {
          ...c,
          siblings: 1,
          systemId: (c as any).systemId || "",
          subSystemId: (c as any).subSystemId || "",
          attribute1: (c as any).attribute1 || "",
          attribute2: (c as any).attribute2 || "",
          attribute3: (c as any).attribute3 || "",
          hasChildMultiplierOverride: false,
        },
        leaf: true,
      };
    });
    return components.sort((a: any, b: any) => (a.data.cadId ? a.data.cadId.localeCompare(b.data.cadId) : 1));
  };

  return { loading, hasCo2MaterialTypeSpecs, co2MaterialTypeSpecKind };
};

const replaceNode = (arr: any[], key: string, searchValue: string, updateValue: any) => {
  if (!arr) return;

  for (let i = 0; i < arr.length; i++) {
    if (arr[i][key] === searchValue) {
      arr[i] = updateValue;
    } else if (arr[i] && Array.isArray(arr[i].children)) {
      replaceNode(arr[i].children, key, searchValue, updateValue);
    }
  }
};

const groupBy = (x: any, f: any) => x.reduce((a: any, b: any, i: any) => ((a[f(b, i, x)] ||= []).push(b), a), {});

export default useModel;
